import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import APFormGroup from 'components/FormFields/APFormGroup';
import Dropdown from 'components/CalendarPicker/Dropdown/';

import {
  fetchWeddingsReminder,
  deleteWeddingsReminder,
  updateWeddingsReminder,
  updateDeltaWeddingsReminder,
  saveDefaultWeddingReminders,
} from 'apollo/projects.query';

const DELTAS = [
  {
    value: 'on-date',
    label: 'On Date',
  },
  {
    value: 'days',
    label: 'Day(s) Before',
  },
  {
    value: 'weeks',
    label: 'Week(s) Before',
  },
  {
    value: 'months',
    label: 'Month(s) Before',
  },
];

const defaultRemindersTasks = [
  {
    id: 0,
    setId: 0,
    spec: {
      delta: {
        days: 2,
      },
    },
  },
  {
    id: 1,
    setId: 1,
    spec: {
      delta: {
        days: 0,
      },
    },
  },
];

const defaultRemindersPayments = [
  {
    id: 0,
    setId: 0,
    spec: {
      delta: {
        weeks: 2,
      },
    },
  },
  {
    id: 1,
    setId: 1,
    spec: {
      delta: {
        days: 3,
      },
    },
  },
  {
    id: 2,
    setId: 2,
    spec: {
      delta: {
        days: 0,
      },
    },
  },
];

const range = (start, end) => {
  return Array(end - start + 1)
    .fill()
    .map((_, idx) => start + idx);
};

const ProjectsSettingsReminders = ({ id }) => {
  const weddingsId = id;
  const numbers = range(1, 15);
  const reminderOptions = DELTAS.map((field) => {
    return field.label;
  });

  const [weddingsRemindersTasks, setWeddingsRemindersTasks] = useState([]);
  const [weddingsRemindersPayments, setWeddingsRemindersPayments] = useState(
    [],
  );

  // Add waiting indicator for when default tasks are being created.
  const [isCreatingTaskReminder, setIsCreatingTaskReminder] = useState(false);
  const [isCreatingPaymentReminder, setIsCreatingPaymentReminder] = useState(false);

  const [remindersTasksUseDefault, setRemindersTasksUseDefault] =
    useState(false);
  const [remindersPaymentsUseDefault, setRemindersPaymentsUseDefault] =
    useState(false);

  const getfetchWeddingsReminders = async (weddingId) => {
    const itemsTasks = await fetchWeddingsReminder(weddingId, 'tasks');
    if (itemsTasks.weddingsReminder === null) {
      setRemindersTasksUseDefault(true);
      setWeddingsRemindersTasks(defaultRemindersTasks);
    } else {
      setRemindersTasksUseDefault(false);
      setWeddingsRemindersTasks(itemsTasks.weddingsReminder);
    }

    const itemsPayments = await fetchWeddingsReminder(weddingId, 'payments');
    if (itemsPayments.weddingsReminder === null) {
      setRemindersPaymentsUseDefault(true);
      setWeddingsRemindersPayments(defaultRemindersPayments);
    } else {
      setRemindersPaymentsUseDefault(false);
      setWeddingsRemindersPayments(itemsPayments.weddingsReminder);
    }

    return Promise.all([itemsTasks, itemsPayments]);
  };

  useEffect(() => {
    getfetchWeddingsReminders(weddingsId);
  }, []);

  const deleteReminder = async (sitId) => {
    await deleteWeddingsReminder(sitId);
  };

  const updateCollections = (reminder, reminderType) => {
    if (reminderType === 'tasks') {
      const index = weddingsRemindersTasks.findIndex(
        (x) => x.id === reminder.id,
      );
      if (index > -1) {
        const reminders = [...weddingsRemindersTasks];
        reminders[index] = reminder;
        setWeddingsRemindersTasks(reminders);
      }
    } else if (reminderType === 'payments') {
      const index = weddingsRemindersPayments.findIndex(
        (x) => x.id === reminder.id,
      );
      if (index > -1) {
        const reminders = [...weddingsRemindersPayments];
        reminders[index] = reminder;
        setWeddingsRemindersPayments(reminders);
      }
    }
  };

  const removeReminder = (sitId, reminderType) => {
    deleteReminder(sitId);

    if (reminderType === 'tasks') {
      const index = weddingsRemindersTasks.findIndex((x) => x.id === sitId);
      if (index > -1) {
        const reminders = [...weddingsRemindersTasks];
        reminders.splice(index, 1);
        setWeddingsRemindersTasks(reminders);
      }
    } else if (reminderType === 'payments') {
      const index = weddingsRemindersPayments.findIndex((x) => x.id === sitId);
      if (index > -1) {
        const reminders = [...weddingsRemindersPayments];
        reminders.splice(index, 1);
        setWeddingsRemindersPayments(reminders);
      }
    }
  };

  const changeReminder = async (reminderType, key, setId) => {
    const reminderBody = {
      reminderType,
      value: key,
      setId,
    };
    const data = await updateWeddingsReminder(weddingsId, reminderBody);
    updateCollections(data.updateWeddingsReminder.reminder, reminderType);
  };

  const updateReminderType = (reminderType, setid, value) => {
    changeReminder(reminderType, value, setid);
  };

  const updateReminderValue = (reminderType, setid, value) => {
    const getCommand = async () => {
      const data = await updateDeltaWeddingsReminder(setid, value);
      updateCollections(
        data.updateDeltaWeddingsReminder.reminder,
        reminderType,
      );
    };
    getCommand(setid, setid, reminderType);
  };

  const addReminder = (key) => {
    const getCommand = async (keyType) => {
      let deltaKey = 'days';
      let deltaValue = 1;
      if (keyType === 'tasks') {
        if (weddingsRemindersTasks.length > 0) {
          const lastRemainder =
            weddingsRemindersTasks[weddingsRemindersTasks.length - 1];
          [deltaKey] = Object.keys(lastRemainder.spec.delta);
          [deltaValue] = Object.values(lastRemainder.spec.delta);
        }

        const reminderBody = {
          reminderType: keyType,
          value: deltaKey,
          count: deltaValue,
        };
        const data = await updateWeddingsReminder(weddingsId, reminderBody);

        setWeddingsRemindersTasks([
          ...weddingsRemindersTasks,
          data.updateWeddingsReminder.reminder,
        ]);
      }
      if (keyType === 'payments') {
        if (weddingsRemindersPayments.length > 0) {
          const lastRemainder =
            weddingsRemindersPayments[weddingsRemindersPayments.length - 1];
          [deltaKey] = Object.keys(lastRemainder.spec.delta);
          [deltaValue] = Object.values(lastRemainder.spec.delta);
        }

        const reminderBody = {
          reminderType: keyType,
          value: deltaKey,
          count: deltaValue,
        };
        await updateWeddingsReminder(weddingsId, reminderBody);
      }
      getfetchWeddingsReminders(weddingsId);
    };
    getCommand(key);
  };

  const updateDefaultReminders = async (key, updatedDefaultReminders) => {
    const reminders = updatedDefaultReminders.map((reminder) => {
      return { id: reminder.id, spec: reminder.spec };
    });
    const data = await saveDefaultWeddingReminders(
      weddingsId,
      key,
      reminders,
    );

    if (data.saveDefaultWeddingReminders.success) {
      return Promise.resolve(await getfetchWeddingsReminders(weddingsId));
    }

    return Promise.resolve();
  };

  const updateDefaultReminderType = (reminderType, sitId, value) => {
    let index = -1;
    let reminders = [];
    if (reminderType === 'tasks') {
      index = weddingsRemindersTasks.findIndex((x) => x.id === sitId);
      reminders = [...weddingsRemindersTasks];
    } else if (reminderType === 'payments') {
      index = weddingsRemindersPayments.findIndex((x) => x.id === sitId);
      reminders = [...weddingsRemindersPayments];
    }
    if (index > -1) {
      const reminder = JSON.parse(
        JSON.stringify(Object.values(reminders)[sitId]),
      );
      const oldDeltaKey = Object.keys(reminder.spec.delta)[0];
      const newDeltaKey = value === 'on-date' ? 'days' : value;
      let [deltaValue] = Object.values(reminder.spec.delta);
      if (value === 'on-date') {
        deltaValue = 0;
      } else if (deltaValue === 0) {
        deltaValue = 1;
      }

      if (oldDeltaKey === newDeltaKey) {
        reminder.spec.delta[newDeltaKey] = deltaValue;
      } else {
        delete Object.assign(reminder.spec.delta, {
          [newDeltaKey]: deltaValue,
        })[oldDeltaKey];
      }

      reminders[index] = reminder;
      updateDefaultReminders(reminderType, reminders);
    }
  };

  const updateDefaultReminderValue = (reminderType, sitId, value) => {
    let index = -1;
    let reminders = [];
    if (reminderType === 'tasks') {
      index = weddingsRemindersTasks.findIndex((x) => x.id === sitId);
      reminders = [...weddingsRemindersTasks];
    } else if (reminderType === 'payments') {
      index = weddingsRemindersPayments.findIndex((x) => x.id === sitId);
      reminders = [...weddingsRemindersPayments];
    }

    if (index > -1) {
      const reminder = JSON.parse(
        JSON.stringify(Object.values(reminders)[sitId]),
      );
      const deltaKey = Object.keys(reminder.spec.delta)[0];
      reminder.spec.delta[deltaKey] = value;
      reminders[index] = reminder;

      updateDefaultReminders(reminderType, reminders);
    }
  };

  const removeDefaultReminder = (sitId, reminderType) => {
    let index = -1;
    let reminders = [];
    if (reminderType === 'tasks') {
      index = weddingsRemindersTasks.findIndex((x) => x.id === sitId);
      reminders = [...weddingsRemindersTasks];
    } else if (reminderType === 'payments') {
      index = weddingsRemindersPayments.findIndex((x) => x.id === sitId);
      reminders = [...weddingsRemindersPayments];
    }

    if (index > -1) {
      reminders.splice(index, 1);
      updateDefaultReminders(reminderType, reminders);
    }
  };

  const renderReminderType = (reminderType, reminderCollection, isDefault) => {
    return (
      <div className="settings-grid__group">
        <div className="settings-grid__category">{reminderType}</div>
        {reminderCollection.length > 0 &&
          reminderCollection.map((reminder) => {
            const key =
              Object.values(reminder.spec.delta)[0] === 0
                ? 'on-date'
                : Object.keys(reminder.spec.delta)[0];
            return (
              <div key={reminder.id}
                className="settings-grid__row"
              >
                <div className="settings-grid__cell">
                  {key !== 'on-date' && (
                    <Dropdown
                      options={numbers}
                      value={`${Object.values(reminder.spec.delta)[0]}`}
                      nShowItems={10}
                      onChangeValue={(newValue) => {
                        const count = Number(newValue);
                        if (isDefault) {
                          updateDefaultReminderValue(
                            reminderType,
                            reminder.id,
                            count,
                          );
                        } else {
                          updateReminderValue(reminderType, reminder.id, count);
                        }
                      }}
                      style={{
                        width: '43px',
                        fontSize: '12px',
                        textAlign: 'center',
                      }}
                    />
                  )}
                  <Dropdown
                    options={reminderOptions}
                    value={DELTAS.find((x) => x.value === key).label}
                    nShowItems={4}
                    onChangeValue={(newValue) => {
                      const newType = DELTAS.find(
                        (x) => x.label === newValue,
                      ).value;
                      if (isDefault) {
                        updateDefaultReminderType(
                          reminderType,
                          reminder.id,
                          newType,
                        );
                      } else {
                        updateReminderType(reminderType, reminder.id, newType);
                      }
                    }}
                  />
                </div>
                <div className="settings-grid__cell">
                  <button
                    onClick={() => {
                      if (isDefault) {
                        removeDefaultReminder(reminder.id, reminderType);
                      } else {
                        removeReminder(reminder.id, reminderType);
                      }
                    }}
                  >
                    <HighlightOffIcon style={{ fontSize: '20px' }} />
                  </button>
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const renderForm = () => {
    return (
      <APFormGroup title="Reminders"
        grid={false}
      >
        <div className="settings-grid__settings">
          {renderReminderType(
            'tasks',
            weddingsRemindersTasks,
            remindersTasksUseDefault,
          )}
          <div>
            <button
              className={clsx('button--unstyled', 'gold', 'button--add', isCreatingTaskReminder && 'loading')}
              disabled={isCreatingTaskReminder}
              onClick={async () => {
                setIsCreatingTaskReminder(true);
                if (remindersTasksUseDefault) {
                  await updateDefaultReminders('tasks', weddingsRemindersTasks);
                }
                addReminder('tasks');
                setIsCreatingTaskReminder(false);
              }}
            >
              Add a Task Reminder
            </button>
          </div>
          {renderReminderType(
            'payments',
            weddingsRemindersPayments,
            remindersPaymentsUseDefault,
          )}
          <div>
            <button
              className={clsx('button--unstyled', 'gold', 'button--add', isCreatingPaymentReminder && 'loading')}
              disabled={isCreatingPaymentReminder}
              onClick={async () => {
                setIsCreatingPaymentReminder(true);
                if (remindersPaymentsUseDefault) {
                  await updateDefaultReminders('payments', weddingsRemindersPayments);
                }
                addReminder('payments');
                setIsCreatingPaymentReminder(false);
              }}
            >
              Add a Payment Reminder
            </button>
          </div>
        </div>
      </APFormGroup>
    );
  };

  return renderForm();
};

export default ProjectsSettingsReminders;
